import isEqual from 'lodash/isEqual';

import { isSafeUrl } from '@atlaskit/adf-schema/is-safe-url';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import { unlinkPayload } from '@atlaskit/editor-common/analytics/linking-utils';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics/types/events';
import type { UnlinkToolbarAEP } from '@atlaskit/editor-common/analytics/types/link-tool-bar-events';
import { SMART_LINK_TYPE } from '@atlaskit/editor-common/analytics/types/node-events';
import type { SmartLinkNodeContext } from '@atlaskit/editor-common/analytics/types/smart-links';
import { addLinkMetadata } from '@atlaskit/editor-common/card';
import type {
	CardReplacementInputMethod,
	EmbedCardNodeTransformer,
	EmbedCardTransformAttrs,
} from '@atlaskit/editor-common/card/types';
import { getActiveLinkMark } from '@atlaskit/editor-common/link/utils';
import { processRawValue } from '@atlaskit/editor-common/process-raw-value';
import type { CardAppearance } from '@atlaskit/editor-common/provider-factory/card-provider';
import type { Command } from '@atlaskit/editor-common/types/command';
import { getAnnotationMarksForPos } from '@atlaskit/editor-common/utils/annotation';
import { nodesBetweenChanged } from '@atlaskit/editor-common/utils/document';
import {
	getLinkCreationAnalyticsEvent,
	isFromCurrentDomain,
} from '@atlaskit/editor-common/utils/hyperlink';
import type {
	Attrs,
	Mark,
	MarkType,
	Node,
	NodeType,
	Schema,
} from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type {
	CardAdf,
	DatasourceAdf,
	DatasourceAdfView,
	InlineCardAdf,
} from '@atlaskit/linking-common/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { closeHistory } from '@atlaskit/prosemirror-history/closeHistory';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import type { CardPluginState, Request } from '../types';
import {
	hideDatasourceModal,
	queueCards,
	removeDatasourceStash,
	resolveCard,
	setDatasourceStash,
} from './actions';
import { pluginKey } from './plugin-key';
import { shouldReplaceLink } from './shouldReplaceLink';
import {
	appearanceForNodeType,
	isDatasourceConfigEditable,
	isDatasourceNode,
	selectedCardAppearance,
} from './utils';

/**
 * Attempt to replace the link into the respective card.
 */
function replaceLinksToCards(
	tr: Transaction,
	cardAdf: Node,
	schema: Schema,
	request: Request,
): string | undefined {
	const { inlineCard } = schema.nodes;
	const { url } = request;

	if (!isSafeUrl(url)) {
		return;
	}

	// replace all the outstanding links with their cards
	const pos = tr.mapping.map(request.pos);
	const $pos = tr.doc.resolve(pos);
	const $head = tr.selection.$head;

	const node = tr.doc.nodeAt(pos);
	if (!node || !node.type.isText) {
		return;
	}
	const replaceLink =
		request.shouldReplaceLink || shouldReplaceLink(node, request.compareLinkText, url);

	if (!replaceLink) {
		return;
	}

	// ED-5638: add an extra space after inline cards to avoid re-rendering them
	const nodes = [cardAdf];

	if (cardAdf.type === inlineCard) {
		nodes.push(schema.text(' '));
	}

	tr.replaceWith(pos, pos + (node.text || url).length, nodes);

	const annotationMarksForPos = getAnnotationMarksForPos($head);

	if (annotationMarksForPos && annotationMarksForPos.length > 0) {
		annotationMarksForPos.forEach((annotationMark) => {
			// Add the annotation mark on to the inlineCard node and the trailing space node.
			tr.addMark(pos, pos + nodes[0].nodeSize + nodes[1].nodeSize, annotationMark);
		});
	}

	return $pos.node($pos.depth - 1).type.name;
}

export const replaceQueuedUrlWithCard =
	(
		url: string,
		cardData: CardAdf | DatasourceAdf,
		analyticsAction?: ACTION,
		editorAnalyticsApi?: EditorAnalyticsAPI,
		createAnalyticsEvent?: CreateUIAnalyticsEvent,
		embedCardNodeTransformer?: EmbedCardNodeTransformer,
	): Command =>
	(editorState, dispatch) => {
		const state = pluginKey.getState(editorState) as CardPluginState | undefined;
		if (!state) {
			return false;
		}

		// find the requests for this URL
		const requests = state.requests.filter((req) => req.url === url);

		// try to transform response to ADF
		const schema: Schema = editorState.schema;
		let cardAdf: Node | null = null;

		// If an embed card transformer is provided and the resolved card is an embedCard,
		// attempt to transform it into an alternative node representation first.
		if (
			cardData.type === 'embedCard' &&
			embedCardNodeTransformer &&
			(fg('platform_native_embeds_rollout_non_maui_experience') ||
				fg('platform_native_embeds_rollout_all_products'))
		) {
			const transformResult = embedCardNodeTransformer(
				schema,
				cardData.attrs as EmbedCardTransformAttrs,
			);
			// The transformer may return a Promise for async transformations (e.g. short-link expansion).
			// When async, we snapshot the current requests and positions now (before any dispatch),
			// then insert the resolved node and clean up the queue in one transaction once the
			// Promise settles. This avoids the stale-editorState problem: rather than re-running
			// replaceQueuedUrlWithCard with an outdated state snapshot, we capture what we need
			// immediately and apply a targeted insert when ready.
			if (transformResult instanceof Promise) {
				// Snapshot requests now, while the queue still contains this URL.
				const pendingRequests = [...requests];
				transformResult.then((resolvedNode) => {
					if (!dispatch) {
						return;
					}
					// Build a fresh transaction (editorState is the snapshot at call-time; positions
					// are mapped through it). For the async case we fall back to cardData if the
					// transform returned nothing, mirroring what the sync path would do.
					const asyncCardAdf = resolvedNode
						? resolvedNode
						: (processRawValue(schema, cardData) ?? null);
					const asyncTr = editorState.tr;
					if (asyncCardAdf) {
						pendingRequests.forEach((request) =>
							replaceLinksToCards(asyncTr, asyncCardAdf, schema, request),
						);
					}
					dispatch(resolveCard(url)(closeHistory(asyncTr)));
				});
				return true;
			}
			cardAdf = transformResult ?? null;
		}

		if (!cardAdf) {
			cardAdf = processRawValue(schema, cardData) ?? null;
		}

		const tr = editorState.tr;

		if (cardAdf) {
			// Should prevent any other node than cards? [inlineCard, blockCard].includes(cardAdf.type)
			const nodeContexts: Array<string | undefined> = requests
				.map((request) => replaceLinksToCards(tr, cardAdf, schema, request))
				.filter((context) => !!context); // context exist

			// Send analytics information
			if (nodeContexts.length) {
				const nodeContext = nodeContexts.every((context) => context === nodeContexts[0])
					? nodeContexts[0]
					: 'mixed';

				/** For block links v1, default to inline links */
				const nodeType = 'inlineCard';
				const [, , domainName] = url.split('/');

				if (state.smartLinkEvents) {
					state.smartLinkEvents.insertSmartLink(domainName, 'inline', createAnalyticsEvent);
				}

				/**
				 * TODO:
				 * What if each request has a different source?
				 * Unlikely, but need to define behaviour.
				 * Ignore analytics event? take first? provide 'mixed' as well?
				 */
				const inputMethod = requests[0].source;
				const sourceEvent = requests[0].sourceEvent;

				editorAnalyticsApi?.attachAnalyticsEvent({
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					action: (analyticsAction as any) || ACTION.INSERTED,
					actionSubject: ACTION_SUBJECT.DOCUMENT,
					actionSubjectId: ACTION_SUBJECT_ID.SMART_LINK,
					eventType: EVENT_TYPE.TRACK,
					attributes: {
						inputMethod,
						nodeType,
						nodeContext: nodeContext as SmartLinkNodeContext,
						fromCurrentDomain: isFromCurrentDomain(url),
					},
					nonPrivacySafeAttributes: {
						domainName,
					},
				})(tr);

				addLinkMetadata(editorState.selection, tr, {
					action: analyticsAction,
					inputMethod,
					cardAction: 'RESOLVE',
					sourceEvent,
				});
			}
		}

		if (dispatch) {
			dispatch(resolveCard(url)(closeHistory(tr)));
		}
		return true;
	};

export const handleFallbackWithAnalytics =
	(request: Request, editorAnalyticsApi: EditorAnalyticsAPI | undefined): Command =>
	(state, dispatch) => {
		const cardState = pluginKey.getState(state) as CardPluginState | undefined;

		if (!cardState) {
			return false;
		}

		const tr = state.tr;

		if (request.source !== INPUT_METHOD.FLOATING_TB) {
			editorAnalyticsApi?.attachAnalyticsEvent(
				getLinkCreationAnalyticsEvent(request.source, request.url),
			)(tr);
		}

		addLinkMetadata(state.selection, tr, {
			action: request.analyticsAction,
			inputMethod: request.source,
			sourceEvent: request.sourceEvent,
		});

		if (dispatch) {
			dispatch(resolveCard(request.url)(tr));
		}
		return true;
	};

/**
 * Shared options used by both `queueCardsFromChangedTr` and
 * `queueCardsFromRange` to build smart-link resolution requests for text
 * nodes carrying a `link` mark.
 */
type CollectLinkRequestsOptions = {
	analyticsAction?: ACTION;
	appearance: CardAppearance;
	linkMarkType: MarkType;
	normalizeLinkText: boolean;
	source: CardReplacementInputMethod;
	sourceEvent: UIAnalyticsEvent | null | undefined;
};

/**
 * Per-node walker shared by `queueCardsFromChangedTr` and
 * `queueCardsFromRange`. Pushes a smart-link resolution request for every
 * text node carrying a qualifying `link` mark.
 *
 * Returning `true`/`false` follows the ProseMirror `nodesBetween`/
 * `nodesBetweenChanged` walker contract: `true` to descend into children,
 * `false` to skip subtree.
 */
const collectLinkRequest = (
	requests: Request[],
	node: Node,
	pos: number,
	options: CollectLinkRequestsOptions,
): boolean => {
	if (!node.isText) {
		return true;
	}

	const linkMark = node.marks.find((mark) => mark.type === options.linkMarkType);

	if (linkMark) {
		if (!shouldReplaceLink(node, options.normalizeLinkText)) {
			return false;
		}

		requests.push({
			url: linkMark.attrs.href,
			pos,
			appearance: options.appearance,
			compareLinkText: options.normalizeLinkText,
			source: options.source,
			analyticsAction: options.analyticsAction,
			sourceEvent: options.sourceEvent,
		});
	}

	return false;
};

export const queueCardsFromChangedTr = (
	state: EditorState,
	tr: Transaction,
	source: CardReplacementInputMethod,
	analyticsAction?: ACTION,
	normalizeLinkText: boolean = true,
	sourceEvent: UIAnalyticsEvent | null | undefined = undefined,
	appearance: CardAppearance = 'inline',
): Transaction => {
	const { schema } = state;
	const { link } = schema.marks;

	const requests: Request[] = [];
	const options: CollectLinkRequestsOptions = {
		analyticsAction,
		appearance,
		linkMarkType: link,
		normalizeLinkText,
		source,
		sourceEvent,
	};

	nodesBetweenChanged(tr, (node, pos) => collectLinkRequest(requests, node, pos, options));

	if (analyticsAction) {
		addLinkMetadata(state.selection, tr, {
			action: analyticsAction,
		});
	}

	return queueCards(requests)(tr);
};

/**
 * Queue link-mark → smart-link resolution for text nodes within an explicit
 * document range, rather than the entire step range of the transaction.
 *
 * Use this instead of `queueCardsFromChangedTr` when you know the exact range
 * that was inserted/modified and want to avoid accidentally queuing pre-existing
 * links that happen to fall within the broader step range.
 */
export const queueCardsFromRange = (
	state: EditorState,
	tr: Transaction,
	from: number,
	to: number,
	source: CardReplacementInputMethod,
	analyticsAction?: ACTION,
	normalizeLinkText: boolean = true,
	sourceEvent: UIAnalyticsEvent | null | undefined = undefined,
	appearance: CardAppearance = 'inline',
): Transaction => {
	const { schema } = state;
	const { link } = schema.marks;

	const requests: Request[] = [];
	const options: CollectLinkRequestsOptions = {
		analyticsAction,
		appearance,
		linkMarkType: link,
		normalizeLinkText,
		source,
		sourceEvent,
	};

	const clampedFrom = Math.max(0, from);
	const clampedTo = Math.min(tr.doc.content.size, to);

	if (clampedFrom < clampedTo) {
		tr.doc.nodesBetween(clampedFrom, clampedTo, (node, pos) =>
			collectLinkRequest(requests, node, pos, options),
		);
	}

	if (analyticsAction) {
		addLinkMetadata(state.selection, tr, {
			action: analyticsAction,
		});
	}

	return queueCards(requests)(tr);
};

export const queueCardFromChangedTr = (
	state: EditorState,
	tr: Transaction,
	source: CardReplacementInputMethod,
	analyticsAction: ACTION,
	normalizeLinkText: boolean = true,
	sourceEvent: UIAnalyticsEvent | null | undefined = undefined,
	previousAppearance?: CardAppearance | 'url',
): Transaction => {
	const { schema } = state;
	const { link } = schema.marks;

	const requests: Request[] = [];

	nodesBetweenChanged(tr, (node, pos) => {
		if (!node.isText) {
			return true;
		}

		const linkMark = node.marks.find((mark) => mark.type === link);

		if (linkMark) {
			if (!shouldReplaceLink(node, normalizeLinkText)) {
				return false;
			}

			requests.push({
				url: linkMark.attrs.href,
				pos,
				appearance: 'inline',
				previousAppearance: previousAppearance,
				compareLinkText: normalizeLinkText,
				source,
				analyticsAction,
				sourceEvent,
			});
		}

		return false;
	});

	addLinkMetadata(state.selection, tr, {
		action: analyticsAction,
	});

	return queueCards(requests)(tr);
};

export const convertHyperlinkToSmartCard = (
	state: EditorState,
	source: CardReplacementInputMethod,
	appearance: CardAppearance,
	normalizeLinkText: boolean = true,
): Transaction => {
	const { schema } = state;
	const { link } = schema.marks;
	const requests: Request[] = [];

	const createRequest = (linkMark: Mark, pos: number): Request => ({
		url: linkMark.attrs.href,
		pos,
		appearance,
		previousAppearance: 'url',
		compareLinkText: normalizeLinkText,
		source,
		analyticsAction: ACTION.CHANGED_TYPE,
		shouldReplaceLink: true,
	});

	if (editorExperiment('platform_editor_controls', 'variant1')) {
		const activeLinkMark = getActiveLinkMark(state);
		if (activeLinkMark) {
			const linkMark = activeLinkMark.node.marks.find((mark) => mark.type === link);
			if (linkMark) {
				requests.push(createRequest(linkMark, activeLinkMark.pos));
			}
		}
	} else {
		state.tr.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
			const linkMark = node.marks.find((mark) => mark.type === link);
			if (linkMark) {
				requests.push(createRequest(linkMark, pos));
			}
		});
	}

	addLinkMetadata(state.selection, state.tr, {
		action: ACTION.CHANGED_TYPE,
	});

	return queueCards(requests)(state.tr);
};

export const changeSelectedCardToLink =
	(
		text?: string,
		href?: string,
		sendAnalytics?: boolean,
		node?: Node,
		pos?: number,
		editorAnalyticsApi?: EditorAnalyticsAPI,
	): Command =>
	(state, dispatch) => {
		const selectedNode =
			state.selection instanceof NodeSelection ? state.selection.node : undefined;

		let tr;
		if (node && pos !== undefined) {
			tr = cardNodeToLinkWithTransaction(state, text, href, node, pos);
		} else {
			tr = cardToLinkWithTransaction(state, text, href);
		}

		updateDatasourceStash(tr, selectedNode);

		if (sendAnalytics) {
			if (selectedNode) {
				editorAnalyticsApi?.attachAnalyticsEvent({
					action: ACTION.CHANGED_TYPE,
					actionSubject: ACTION_SUBJECT.SMART_LINK,
					eventType: EVENT_TYPE.TRACK,
					attributes: {
						newType: SMART_LINK_TYPE.URL,
						previousType: appearanceForNodeType(selectedNode.type),
					},
				} as AnalyticsEventPayload)(tr);
			}
		}

		if (dispatch) {
			dispatch(tr.scrollIntoView());
		}

		return true;
	};

export const changeSelectedCardToLinkFallback =
	(
		text?: string,
		href?: string,
		sendAnalytics?: boolean,
		node?: Node,
		pos?: number,
		editorAnalyticsApi?: EditorAnalyticsAPI,
	): Command =>
	(state, dispatch) => {
		let tr;
		if (node && pos !== undefined) {
			tr = cardNodeToLinkWithTransaction(state, text, href, node, pos);
		} else {
			tr = cardToLinkWithTransaction(state, text, href);
		}
		if (sendAnalytics) {
			editorAnalyticsApi?.attachAnalyticsEvent({
				action: ACTION.ERRORED,
				actionSubject: ACTION_SUBJECT.SMART_LINK,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					error: 'Smart card falling back to link.',
				},
			} as AnalyticsEventPayload)(tr);
		}

		if (dispatch) {
			dispatch(tr.setMeta('addToHistory', false));
		}

		return true;
	};

export const updateCard =
	(href: string, sourceEvent?: UIAnalyticsEvent | null | undefined): Command =>
	(state, dispatch) => {
		const selectedNode = state.selection instanceof NodeSelection && state.selection.node;
		if (!selectedNode) {
			return false;
		}

		const cardAppearance = selectedCardAppearance(state);

		const tr = cardToLinkWithTransaction(state, href, href);

		queueCardFromChangedTr(
			state,
			tr,
			INPUT_METHOD.MANUAL,
			ACTION.UPDATED,
			undefined,
			sourceEvent,
			cardAppearance,
		);

		if (dispatch) {
			dispatch(tr.scrollIntoView());
		}

		return true;
	};

function cardToLinkWithTransaction(
	state: EditorState,
	text: string | undefined,
	href: string | undefined,
): Transaction {
	const selectedNode = state.selection instanceof NodeSelection && state.selection.node;
	if (!selectedNode) {
		return state.tr;
	}
	const { link } = state.schema.marks;
	const url = selectedNode.attrs.url || selectedNode.attrs.data.url;
	const tr = state.tr.replaceSelectionWith(
		state.schema.text(text || url, [link.create({ href: href || url })]),
		false,
	);
	return tr;
}

function cardNodeToLinkWithTransaction(
	state: EditorState,
	text: string | undefined,
	href: string | undefined,
	node: Node,
	pos: number,
): Transaction {
	const { link } = state.schema.marks;
	const url = node.attrs.url || node.attrs.data.url;
	return state.tr.replaceWith(
		pos,
		pos + node.nodeSize,
		state.schema.text(text || url, [link.create({ href: href || url })]),
	);
}

export const changeSelectedCardToText =
	(text: string, editorAnalyticsApi: EditorAnalyticsAPI | undefined): Command =>
	(state, dispatch) => {
		const selectedNode = state.selection instanceof NodeSelection && state.selection.node;
		if (!selectedNode) {
			return false;
		}

		const tr = state.tr.replaceSelectionWith(state.schema.text(text), false);

		if (dispatch) {
			addLinkMetadata(state.selection, tr, {
				action: ACTION.UNLINK,
			});
			tr.scrollIntoView();
			editorAnalyticsApi?.attachAnalyticsEvent(
				unlinkPayload(ACTION_SUBJECT_ID.CARD_INLINE) as UnlinkToolbarAEP,
			)(tr);
			dispatch(tr);
		}

		return true;
	};

export const setSelectedCardAppearance: (
	appearance: CardAppearance,
	editorAnalyticsApi: EditorAnalyticsAPI | undefined,
) => Command = (appearance, editorAnalyticsApi) => (state, dispatch) => {
	const selectedNode = state.selection instanceof NodeSelection ? state.selection.node : undefined;

	if (!selectedNode) {
		// When there is no selected node, we insert a new one
		// and replace the existing blue link
		const tr = convertHyperlinkToSmartCard(state, INPUT_METHOD.FLOATING_TB, appearance);
		if (dispatch) {
			addLinkMetadata(state.selection, tr, {
				action: ACTION.CHANGED_TYPE,
			});
			dispatch(tr.scrollIntoView());
		}

		return false;
	}

	if (appearanceForNodeType(selectedNode.type) === appearance && !selectedNode.attrs.datasource) {
		return false;
	}

	const attrs = getAttrsForAppearance(
		appearance,
		selectedNode,
		state.selection.$from.parent.type.name === 'bodiedSyncBlock',
	);

	const { from, to } = state.selection;
	const nodeType = getLinkNodeType(appearance, state.schema.nodes as LinkNodes);
	const tr = state.tr.setNodeMarkup(from, nodeType, attrs, selectedNode.marks);

	// If switching to embed appearance, attempt to use a registered transform command
	// to create an alternative node representation (e.g. a native embed).
	if (appearance === 'embed' && (selectedNode.attrs.url || selectedNode.attrs.data?.url)) {
		const cardState = pluginKey.getState(state) as CardPluginState | undefined;
		const createEmbedCardTransformCommand =
			cardState?.embedCardTransformers?.createEmbedCardTransformCommand;
		if (createEmbedCardTransformCommand) {
			const transformCommand = createEmbedCardTransformCommand({
				editorAnalyticsApi,
				augmentTransaction: (augmentTr: Transaction) => {
					updateDatasourceStash(augmentTr, selectedNode);
					editorAnalyticsApi?.attachAnalyticsEvent({
						action: ACTION.CHANGED_TYPE,
						actionSubject: ACTION_SUBJECT.SMART_LINK,
						eventType: EVENT_TYPE.TRACK,
						attributes: {
							newType: appearance as SMART_LINK_TYPE,
							previousType: appearanceForNodeType(selectedNode.type),
						},
					} as AnalyticsEventPayload)(augmentTr);
					addLinkMetadata(state.selection, augmentTr, {
						action: ACTION.CHANGED_TYPE,
					});
				},
			});
			if (transformCommand(state, dispatch)) {
				return true;
			}
		}
	}

	updateDatasourceStash(tr, selectedNode);

	// When the selected card is the last element in the doc we add a new paragraph after it for consistent replacement
	if (tr.doc.nodeSize - 2 === to) {
		tr.insertText(' ', to);
	}

	tr.setSelection(TextSelection.create(tr.doc, to + 1));
	const previousNodePos = from - 1 > 0 ? from - 1 : 0;
	const previousNode = tr.doc.nodeAt(previousNodePos);
	if (previousNode?.type?.name === 'paragraph') {
		tr.delete(previousNodePos, from);
	}

	editorAnalyticsApi?.attachAnalyticsEvent({
		action: ACTION.CHANGED_TYPE,
		actionSubject: ACTION_SUBJECT.SMART_LINK,
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			newType: appearance,
			previousType: appearanceForNodeType(selectedNode.type),
		},
	} as AnalyticsEventPayload)(tr);

	addLinkMetadata(state.selection, tr, {
		action: ACTION.CHANGED_TYPE,
	});

	if (dispatch) {
		dispatch(tr.scrollIntoView());
	}

	return true;
};

export type LinkNodes = {
	[key in 'inlineCard' | 'blockCard' | 'embedCard']: NodeType;
};

export const getLinkNodeType = (appearance: CardAppearance, linkNodes: LinkNodes): NodeType => {
	switch (appearance) {
		case 'inline':
			return linkNodes.inlineCard;
		case 'block':
			return linkNodes.blockCard;
		case 'embed':
			return linkNodes.embedCard;
	}
};

type UpdateCardArgs = {
	inputMethod?: string;
	isDeletingConfig?: boolean;
	newAdf: DatasourceAdf | InlineCardAdf;
	node: Node;
	sourceEvent?: UIAnalyticsEvent;
	state: EditorState;
	view: EditorView;
};

// Apply an update made from a datasource ui interaction
export const updateCardViaDatasource = (args: UpdateCardArgs): void => {
	const { state, node, newAdf, view, sourceEvent, isDeletingConfig, inputMethod } = args;
	const {
		tr,
		selection: { from, to },
		schema: { nodes: schemaNodes },
	} = state;

	if (newAdf.type === 'blockCard') {
		if (node.attrs?.datasource && newAdf.attrs?.datasource) {
			const newAttrs = newAdf.attrs;
			const oldAttrs = node.attrs as DatasourceAdf['attrs'];

			const [newViews] = (newAttrs.datasource.views as DatasourceAdfView[]) ?? [];
			const [oldViews] = (oldAttrs.datasource.views as DatasourceAdfView[]) ?? [];

			const isColumnChange = !isEqual(oldViews?.properties?.columns, newViews?.properties?.columns);
			const isUrlChange = newAttrs.url !== oldAttrs.url;
			const isParametersChange =
				fg('platform_lp_sllv_preserve_assets_columns') &&
				!isEqual(oldAttrs.datasource.parameters, newAttrs.datasource.parameters);

			if (isColumnChange || isUrlChange || isParametersChange) {
				tr.setNodeMarkup(from, schemaNodes.blockCard, {
					...oldAttrs,
					...newAdf.attrs,
				});
			}
		} else if (node.type.isText) {
			// url to datasource
			let link: { pos: number; text: string | undefined; url: string } | undefined;
			state.doc.nodesBetween(from, to, (node, pos) => {
				// get the actual start position of a link within the node
				const linkMark = node.marks.find((mark) => mark.type === state.schema.marks.link);
				if (linkMark) {
					link = {
						url: linkMark.attrs.href,
						text: node.text,
						pos,
					};
					return false;
				}
				return true;
			});

			if (link) {
				const newNode = schemaNodes.blockCard.createChecked(newAdf.attrs);
				tr.replaceWith(link.pos, link.pos + (link.text || link.url).length, [newNode]);
			}
		} else {
			// inline or blockCard to datasource
			tr.setNodeMarkup(from, schemaNodes.blockCard, newAdf.attrs);
		}
	} else if (newAdf.type === 'inlineCard') {
		// card type to inlineCard
		tr.setNodeMarkup(from, schemaNodes.inlineCard, newAdf.attrs);
	}

	addLinkMetadata(state.selection, tr, {
		action: ACTION.UPDATED,
		sourceEvent,
		inputMethod,
	});

	if (isDeletingConfig) {
		if (typeof node.attrs.url === 'string') {
			removeDatasourceStash(tr, node.attrs.url);
		}
	} else {
		hideDatasourceModal(tr);
	}
	view.dispatch(tr.scrollIntoView());
};

export const insertDatasource = (
	state: EditorState,
	adf: DatasourceAdf | InlineCardAdf,
	view: EditorView,
	sourceEvent?: UIAnalyticsEvent,
): void => {
	const {
		tr,
		selection: { from },
		schema: { nodes: schemaNodes },
	} = state;

	const { attrs, type } = adf;

	const schemaNode = type === 'inlineCard' ? schemaNodes.inlineCard : schemaNodes.blockCard;
	const newNode = schemaNode.createChecked(attrs);
	// in future, if we decide to do datasource insertion from the main toolbar, we should probably consider editor-plugin-content-insertion instead of tr.insert
	// this will allow us to deal with insertions from multiple paths in a more consistent way
	newNode && tr.insert(from, newNode);

	hideDatasourceModal(tr);
	addLinkMetadata(state.selection, tr, {
		action: ACTION.INSERTED,
		sourceEvent,
	});
	view.dispatch(tr.scrollIntoView());
};

/**
 * Get attributes for new Card Appearance
 */
export const getAttrsForAppearance = (
	appearance: CardAppearance,
	selectedNode: Node,
	isInsideBodiedSyncBlock: boolean = false,
): Attrs => {
	if (appearance === 'embed') {
		return {
			...selectedNode.attrs,
			layout: 'center',
			...(isInsideBodiedSyncBlock
				? // When converting to embed, width attribute is set to null and when the document is published, the width attribute is set to 100 as per schema default
					// For editor, width is not required to render the embed card, but it's required in renderer
					// Because sync block has nested renderer in editor, we need width to be defined even in editor so embed in reference sync block can be rendered properly
					{ width: selectedNode.attrs.width ?? 100 }
				: {}),
		};
	}

	if (isDatasourceNode(selectedNode)) {
		return { url: selectedNode.attrs.url };
	}

	return selectedNode.attrs;
};

const updateDatasourceStash = (tr: Transaction, selectedNode?: Node) => {
	if (
		isDatasourceNode(selectedNode) &&
		!isDatasourceConfigEditable(selectedNode.attrs.datasource.id) &&
		selectedNode.attrs.url
	) {
		setDatasourceStash(tr, {
			url: selectedNode.attrs.url,
			views: selectedNode.attrs.datasource.views,
		});
	}
};
